import { Component, OnInit } from '@angular/core';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../../../core/auto-unsubscribe';
import { RequestTypeEnum } from '../../../shared/xatena-api/request-type';
import { RequestTypeBaseComponent } from '../request-type-base/request-type-base.component';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { DemandRequestDetailService } from '../demand-request-detail.service';
import { of } from 'rxjs';
import { RequestDto } from '../../../xatena-api';
import { FormlyModule } from '@ngx-formly/core';
import { AsyncPipe } from '@angular/common';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

@AutoUnsubscribe()
@Component({
  selector: 'app-request-type-test-position-investment',
  templateUrl: './request-type-test-position-investment.component.html',
  styleUrls: ['./request-type-test-position-investment.component.scss'],
  imports: [FormlyModule, AsyncPipe, MatDatepickerModule, MatNativeDateModule],
})
export class RequestTypeTestPositionInvestmentComponent
  extends RequestTypeBaseComponent
  implements OnInit
{
  constructor(
    protected translateService: TranslateService,
    demandRequestDetailService: DemandRequestDetailService
  ) {
    super(demandRequestDetailService);
  }

  subs = new SubSink();

  public get procurementTypeSelectList$() {
    const options: {
      key: number;
      value: string;
    }[] = [
      {
        key: 0,
        value: this.translateService.instant(
          'SHARED.PROCUREMENT_TYPE_SELECT.BUY'
        ),
      },
      {
        key: 1,
        value: this.translateService.instant(
          'SHARED.PROCUREMENT_TYPE_SELECT.CONSIGNMENT'
        ),
      },
      {
        key: 2,
        value: this.translateService.instant(
          'SHARED.PROCUREMENT_TYPE_SELECT.RENTAL'
        ),
      },
    ];

    return of(options);
  }

  protected updateTotalCost(request: RequestDto) {
    // NOOP
  }

  ngOnInit(): void {
    this.subs.sink = this.form.valueChanges.subscribe(s => {
      this.updateTotalCost(s);
    });

    this.subs.sink = this.demandRequestDetailService.requestProducts$.subscribe(
      requestProducts => {
        let costTotal = 0;
        let costTest = 0;
        if (requestProducts) {
          costTotal = requestProducts.reduce(
            (p, c) => p + +(c.amount ?? 0) * +(c.priceBaseUnit ?? 0),
            0
          );

          costTest = requestProducts.reduce(
            (p, c) => p + +(c.testAmount ?? 0) * +(c.priceBaseUnit ?? 0),
            0
          );
        }

        this.form.get('costTotal')?.setValue(costTotal, {
          emitEvent: false,
        });

        this.form.get('costTest')?.setValue(costTest, {
          emitEvent: false,
        });
      }
    );

    this.fields = [
      {
        hooks: {
          onInit: field => {
            this.subs.sink = this.isReadOnly$.subscribe(s => {
              if (s) {
                this.form.disable();
              } else {
                this.form.enable();
              }
            });
          },
        },
        hideExpression: model =>
          !(model.requestTypeId === RequestTypeEnum.TestPositionInvestment),
        fieldGroup: [
          {
            wrappers: ['mat-card'],
            templateOptions: {
              label: _(
                'DEMAND_REQUEST.DETAIL.FORM.REQUEST_TYPE_11_GRP_1.LABEL'
              ),
              translate: true,
            },
            fieldGroup: [
              {
                key: 'description',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.LABEL'),
                  description: _(
                    'DEMAND_REQUEST.DETAIL.FORM.DESCRIPTION.DESCRIPTION_RT11'
                  ),
                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'reason',
                type: 'textarea',
                templateOptions: {
                  required: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.REASON.LABEL'),

                  translate: true,
                  autosize: true,
                  autosizeMinRows: 3,
                  autosizeMaxRows: 7,
                },
              },
              {
                key: 'id',
                type: 'request-products-grid',
                templateOptions: {
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PRODUCTS.LABEL'),
                  translate: true,
                  currency: this.currency$,
                  requestTypeId: this.requestTypeId$,
                  colDef: [
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.TITLE'
                      ),
                      field: 'title',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.SUPPLIER_NAME'
                      ),
                      field: 'supplierName',
                      flex: 1,
                    },
                    {
                      field: 'supplierContactName',
                      hide: true,
                    },
                    {
                      field: 'supplierContactPhoneNumber',
                      hide: true,
                    },
                    {
                      field: 'supplierContactMail',
                      hide: true,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.SUPPLIER_CONTACT'
                      ),

                      cellRenderer: 'supplierContactCellRenderer',
                      autoHeight: true,
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.SUPPLIER_ARTICLE_NUMBER'
                      ),
                      field: 'supplierArticleNumber',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.TEST_AMOUNT'
                      ),
                      field: 'testAmount',
                      flex: 1,
                    },
                    {
                      headerName: _(
                        'DEMAND_REQUEST.DETAIL.PRODUCTS.GRID.COL.AMOUNT_RT11'
                      ),
                      field: 'amount',
                      flex: 1,
                    },
                  ],
                },
              },
              {
                key: 'costTest',
                type: 'currency',
                templateOptions: {
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TEST.LABEL'),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'costTotal',
                type: 'currency',
                templateOptions: {
                  readonly: true,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.COST_TOTAL.LABEL_RT11'),
                  translate: true,
                  currency: this.currency$,
                },
              },
              {
                key: 'procurementType',
                type: 'select',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.PROCUREMENT_TYPE.LABEL'),
                  translate: true,
                  options: this.procurementTypeSelectList$,
                  valueProp: 'key',
                  labelProp: 'value',
                },
              },
              {
                key: 'location',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.LOCATION.LABEL'),
                  translate: true,
                },
              },
              {
                key: 'testDurationStart',
                type: 'datepicker',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.TEST_DURATION_START.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'testDurationEnd',
                type: 'datepicker',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _(
                    'DEMAND_REQUEST.DETAIL.FORM.TEST_DURATION_END.LABEL'
                  ),
                  translate: true,
                },
              },
              {
                key: 'usageByOthers',
                type: 'input',
                templateOptions: {
                  required: false,
                  appearance: this.formFieldAppearance,
                  label: _('DEMAND_REQUEST.DETAIL.FORM.USAGE_BY_OTHERS.LABEL'),
                  translate: true,
                },
              },
            ],
          },
        ],
      },
    ];
  }
}
